import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { CheckoutContext } from '@components/checkout/Context';
import Colors from '@components/shared/Colors';
import FadeIn from '@components/shared/FadeIn';
import { P } from '@components/shared/Typography';
import { CurrencyObj } from '@contracts';

interface Props {
  discountValue: number;
  currency: CurrencyObj;
  showDiscountValue: boolean;
}

const Discount: React.FC<Props> = ({ discountValue, currency, showDiscountValue }) => {
  const { t } = useTranslation();
  const { checkoutState } = useContext(CheckoutContext);
  const { coupon } = checkoutState;

  const getDiscountAmountText = () => {
    if (!coupon) return '';
    if (coupon.discount.type === 'percent') {
      return `${coupon.discount.percent}%`;
    }
    const discountForCurrency = coupon.discount.currencies.find((a) => a.currency === currency.letterCode);
    const discountAmount = discountForCurrency?.amount ?? coupon.discount.currencies[0].amount;
    return `${currency.symbol}${discountAmount.toFixed(2)}`;
  };

  if (!coupon && !(discountValue && showDiscountValue)) return null;

  return (
    <Container>
      <FadeIn duration={400}>
        <DiscountImageWrap>%</DiscountImageWrap>
        <CartItemInfo>
          <CartItemTitle>
            <ItemText>
              {coupon && (
                <>
                  <span>{t('components.discount-amount', { amount: getDiscountAmountText() })}</span>
                  <span className="subtitle">{t('components.discount-code', { code: coupon?.code?.toUpperCase() })}</span>
                </>
              )}
              {showDiscountValue && discountValue && <span>{t('components.discount')}</span>}
            </ItemText>
          </CartItemTitle>
        </CartItemInfo>
        <CartItemPrice>
          <span className="price">
            -{currency.symbol}
            {discountValue?.toFixed(2)}
          </span>
        </CartItemPrice>
      </FadeIn>
    </Container>
  );
};

export default Discount;

const Container = styled.div`
  &:first-child {
    margin-top: 0;
  }

  & > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const DiscountImageWrap = styled.div`
  width: 40px;
  min-width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 15px;
  background: ${Colors.gold};
  color: ${Colors.cream};
  text-align: center;
  line-height: 40px;
  font-size: 20px;
  font-weight: bold;
  font-family: 'BrownStd';
`;

const CartItemInfo = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const CartItemTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-grow: 1;
`;

const ItemText = styled.h5`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 22px;
  font-weight: normal;
  font-family: 'BrownStd';
  color: ${Colors.navyBlue};

  .subtitle {
    font-weight: 400;
    font-size: 13px;
    line-height: 16px;
    opacity: 0.5;
    margin-top: 1px;

    &:empty {
      display: none;
    }
    
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CartItemPrice = styled(P)`
  display: flex;
  flex-direction: column-reverse;
  font-size: 14px;
  line-height: 22px;
  font-family: 'BrownStd';
  font-weight: 700;
  min-width: 60px;
  margin-left: 5px;
  text-align: center;
  color: ${Colors.navyBlue};

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 17px;
    min-width: 54px;
  }

  & span.original-price {
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    text-decoration-line: line-through;
    opacity: 0.5;
  }

  & span.original-price:not(:empty) + span.price {
    color: ${Colors.gold};
  }
`;
